/**
 * Shared nav link rendering (APP-001 §14).
 *
 * Used by both the desktop Sidebar and the MobileNavigation drawer so the two
 * stay visually and semantically consistent. External links open in a new tab
 * with rel safeguards; non-authoritative status hints render as small badges.
 */
import { NavLink } from 'react-router-dom';
import type { NavItem } from '@/config/navigation';
import { HINT_LABELS } from '@/config/navigation';
import { Badge } from '@/components/ui/badge';
import { ExternalLink } from '@/components/icons';
import { cn } from '@/lib/utils';

export function NavItemLink({ item, onNavigate }: { item: NavItem; onNavigate?: () => void }) {
  const Icon = item.icon;
  const label = HINT_LABELS();
  const hintLabel = item.hint ? label[item.hint] : null;

  const content = (
    <>
      <Icon className="h-4 w-4 shrink-0" />
      <span className="flex-1 truncate">{item.label}</span>
      {hintLabel ? (
        <Badge
          variant={item.hint === 'external' ? 'outline' : 'secondary'}
          className="ml-auto text-[10px] font-normal"
        >
          {item.hint === 'external' ? <ExternalLink className="mr-1 h-3 w-3" /> : null}
          {hintLabel}
        </Badge>
      ) : null}
    </>
  );

  if (item.external) {
    return (
      <a
        href={item.to}
        target="_blank"
        rel="noopener noreferrer"
        onClick={onNavigate}
        className={cn(
          'flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none',
        )}
        title={item.description}
      >
        {content}
      </a>
    );
  }

  return (
    <NavLink
      to={item.to}
      onClick={onNavigate}
      title={item.description}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors focus-visible:outline-none',
          isActive
            ? 'bg-primary/10 text-primary'
            : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
        )
      }
    >
      {content}
    </NavLink>
  );
}
